import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import logoImage from '../img/logo.png';
import featureImage1 from '../img/placeholder-agenda.png';
import featureImage2 from '../img/placeholder-financeiro.png';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendarCheck, faChartPie, faBoxesStacked, faFileMedical,
    faInfinity, faUsers, faBoxOpen, faCheck, faStethoscope,
    faConciergeBell, faUserShield, faBars, faTimes,
    faArrowRight, faPlay, faStar, faShieldAlt, faClock,
} from '@fortawesome/free-solid-svg-icons';

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
    primary:     '#2563eb',
    primaryLight:'#3b82f6',
    primaryDark: '#1d4ed8',
    success:     '#10b981',
    successDark: '#059669',
    teal:        '#0d9488',
    gray50:      '#f9fafb',
    gray100:     '#f3f4f6',
    gray200:     '#e5e7eb',
    gray300:     '#d1d5db',
    gray400:     '#9ca3af',
    gray500:     '#6b7280',
    gray700:     '#374151',
    gray800:     '#1f2937',
    gray900:     '#111827',
    white:       '#ffffff',
};

// ─── Hook: detectar scroll ────────────────────────────────────────────────────
const useScrolled = (threshold = 20) => {
    const [scrolled, setScrolled] = useState(false);
    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > threshold);
        window.addEventListener('scroll', handler);
        return () => window.removeEventListener('scroll', handler);
    }, [threshold]);
    return scrolled;
};

// ─── Hook: intersection observer (animações ao entrar na tela) ────────────────
const useInView = () => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setInView(true); },
            { threshold: 0.15 }
        );
        if (ref.current) observer.observe(ref.current);
        return () => observer.disconnect();
    }, []);
    return [ref, inView];
};

// ─── Componente: Navbar ───────────────────────────────────────────────────────
const Navbar = () => {
    const scrolled   = useScrolled();
    const [open, setOpen] = useState(false);

    return (
        <nav style={{
            position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000,
            backgroundColor: scrolled ? 'rgba(255,255,255,0.95)' : 'transparent',
            backdropFilter: scrolled ? 'blur(12px)' : 'none',
            borderBottom: scrolled ? `1px solid ${C.gray200}` : 'none',
            transition: 'all 0.3s ease',
            padding: '0 24px',
        }}>
            <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '72px' }}>
                {/* Logo */}
                <Link to="/inicio" style={{ textDecoration: 'none' }}>
                    <img src={logoImage} alt="FluxoClínica" style={{ maxHeight: '44px', objectFit: 'contain' }} />
                </Link>

                {/* Links desktop */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }} className="nav-links-desktop">
                    {[
                        { label: 'Funcionalidades', href: '#features' },
                        { label: 'Módulos',         href: '#modules'  },
                        { label: 'Preços',          href: '#pricing'  },
                    ].map(link => (
                        <a key={link.label} href={link.href} style={{ textDecoration: 'none', fontSize: '14px', fontWeight: '500', color: scrolled ? C.gray700 : C.white, transition: 'color 0.2s' }}
                            onMouseEnter={e => e.target.style.color = C.primaryLight}
                            onMouseLeave={e => e.target.style.color = scrolled ? C.gray700 : C.white}
                        >
                            {link.label}
                        </a>
                    ))}
                </div>

                {/* CTA desktop */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} className="nav-cta-desktop">
                    <Link to="/login" style={{ textDecoration: 'none', fontSize: '14px', fontWeight: '600', color: scrolled ? C.gray700 : C.white, padding: '8px 16px', borderRadius: '8px', transition: 'all 0.2s' }}>
                        Entrar
                    </Link>
                    <Link to="/register" style={{
                        textDecoration: 'none', fontSize: '14px', fontWeight: '700',
                        color: C.white, padding: '10px 20px', borderRadius: '10px',
                        background: `linear-gradient(135deg, ${C.success}, ${C.successDark})`,
                        boxShadow: `0 4px 14px ${C.success}50`,
                        transition: 'all 0.2s',
                    }}>
                        Cadastre-se Grátis
                    </Link>
                </div>

                {/* Hamburguer mobile */}
                <button onClick={() => setOpen(o => !o)} style={{ display: 'none', background: 'none', border: 'none', cursor: 'pointer', color: scrolled ? C.gray700 : C.white, fontSize: '20px' }} className="nav-hamburger">
                    <FontAwesomeIcon icon={open ? faTimes : faBars} />
                </button>
            </div>

            {/* Menu mobile */}
            {open && (
                <div style={{ backgroundColor: C.white, borderTop: `1px solid ${C.gray200}`, padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {['Funcionalidades', 'Módulos', 'Preços'].map(l => (
                        <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setOpen(false)} style={{ textDecoration: 'none', fontSize: '15px', fontWeight: '500', color: C.gray700, padding: '8px 0', borderBottom: `1px solid ${C.gray100}` }}>
                            {l}
                        </a>
                    ))}
                    <Link to="/register" onClick={() => setOpen(false)} style={{ textDecoration: 'none', textAlign: 'center', padding: '12px', borderRadius: '10px', background: `linear-gradient(135deg, ${C.success}, ${C.successDark})`, color: C.white, fontWeight: '700', fontSize: '15px', marginTop: '8px' }}>
                        Cadastre-se Grátis
                    </Link>
                </div>
            )}
        </nav>
    );
};

// ─── Componente: StatBadge ────────────────────────────────────────────────────
const StatBadge = ({ value, label }) => (
    <div style={{ textAlign: 'center', padding: '16px 24px', backgroundColor: 'rgba(255,255,255,0.1)', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.2)', backdropFilter: 'blur(8px)' }}>
        <p style={{ margin: 0, fontSize: '28px', fontWeight: '800', color: C.white }}>{value}</p>
        <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.75)', marginTop: '2px' }}>{label}</p>
    </div>
);

// ─── Componente: ModuleCard ───────────────────────────────────────────────────
const ModuleCard = ({ icon, name, desc, color }) => {
    const [hovered, setHovered] = useState(false);
    return (
        <div
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                padding: '28px 20px',
                backgroundColor: C.white,
                borderRadius: '16px',
                border: `1px solid ${hovered ? color + '50' : C.gray200}`,
                boxShadow: hovered ? `0 12px 32px ${color}20` : '0 2px 8px rgba(0,0,0,0.04)',
                transition: 'all 0.3s ease',
                transform: hovered ? 'translateY(-4px)' : 'none',
                textAlign: 'center',
                cursor: 'default',
            }}
        >
            <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: `linear-gradient(135deg, ${color}20, ${color}10)`, border: `1px solid ${color}30`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px', fontSize: '24px', color }}>
                <FontAwesomeIcon icon={icon} />
            </div>
            <p style={{ margin: '0 0 6px', fontSize: '14px', fontWeight: '700', color: C.gray900 }}>{name}</p>
            {desc && <p style={{ margin: 0, fontSize: '12px', color: C.gray400, lineHeight: '1.4' }}>{desc}</p>}
        </div>
    );
};

// ─── Componente: FeatureSection ───────────────────────────────────────────────
const FeatureSection = ({ feature, index }) => {
    const [ref, inView] = useInView();
    const isEven = index % 2 === 0;

    return (
        <div
            ref={ref}
            style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '64px',
                alignItems: 'center',
                marginBottom: '96px',
                opacity: inView ? 1 : 0,
                transform: inView ? 'translateY(0)' : 'translateY(32px)',
                transition: 'all 0.7s ease',
            }}
            className="feature-grid"
        >
            {/* Texto */}
            <div style={{ order: isEven ? 1 : 2 }} className="feature-text">
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 14px', borderRadius: '999px', backgroundColor: `${C.primaryLight}15`, border: `1px solid ${C.primaryLight}30`, marginBottom: '16px' }}>
                    <FontAwesomeIcon icon={feature.icon} style={{ color: C.primaryLight, fontSize: '13px' }} />
                    <span style={{ fontSize: '12px', fontWeight: '700', color: C.primaryLight, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        Funcionalidade
                    </span>
                </div>

                <h3 style={{ fontSize: 'clamp(22px, 3vw, 30px)', fontWeight: '800', color: C.gray900, margin: '0 0 16px', lineHeight: '1.3' }}>
                    {feature.title}
                </h3>

                {Array.isArray(feature.description) ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                        {feature.description.map((item, i) => (
                            <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                <div style={{ width: '20px', height: '20px', borderRadius: '50%', backgroundColor: `${C.success}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>
                                    <FontAwesomeIcon icon={faCheck} style={{ fontSize: '9px', color: C.success }} />
                                </div>
                                <span style={{ fontSize: '14px', color: C.gray500, lineHeight: '1.5' }}>{item}</span>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ fontSize: '15px', color: C.gray500, lineHeight: '1.7', margin: 0 }}>{feature.description}</p>
                )}

                <Link to="/register" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    marginTop: '28px', padding: '11px 22px',
                    background: `linear-gradient(135deg, ${C.primary}, ${C.primaryDark})`,
                    color: C.white, textDecoration: 'none', borderRadius: '10px',
                    fontSize: '13px', fontWeight: '700',
                    boxShadow: `0 4px 14px ${C.primary}40`,
                }}>
                    Experimentar grátis
                    <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '11px' }} />
                </Link>
            </div>

            {/* Mídia */}
            <div style={{ order: isEven ? 2 : 1, borderRadius: '20px', overflow: 'hidden', boxShadow: '0 24px 64px rgba(0,0,0,0.12)', border: `1px solid ${C.gray200}` }} className="feature-media">
                {feature.mediaType === 'video' ? (
                    <div style={{ position: 'relative', paddingBottom: '56.25%', height: 0, backgroundColor: C.gray900 }}>
                        <iframe
                            src={feature.mediaUrl}
                            title={feature.title}
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                        />
                    </div>
                ) : (
                    <img src={feature.mediaUrl} alt={feature.title} style={{ width: '100%', height: 'auto', display: 'block' }} />
                )}
            </div>
        </div>
    );
};

// ─── Componente: PricingCard ──────────────────────────────────────────────────
const PricingCard = ({ icon, title, description, highlight }) => (
    <div style={{
        padding: '32px',
        backgroundColor: C.white,
        borderRadius: '20px',
        border: highlight ? `2px solid ${C.success}` : `1px solid ${C.gray200}`,
        boxShadow: highlight ? `0 20px 48px ${C.success}20` : '0 4px 16px rgba(0,0,0,0.05)',
        position: 'relative',
        overflow: 'hidden',
        flex: 1,
    }}>
        {highlight && (
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '4px', background: `linear-gradient(90deg, ${C.success}, ${C.teal})` }} />
        )}
        <div style={{ width: '56px', height: '56px', borderRadius: '14px', background: `linear-gradient(135deg, ${C.success}20, ${C.success}10)`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', fontSize: '24px', color: C.success }}>
            <FontAwesomeIcon icon={icon} />
        </div>
        <h4 style={{ fontSize: '18px', fontWeight: '700', color: C.gray900, margin: '0 0 10px' }}>{title}</h4>
        <p style={{ fontSize: '14px', color: C.gray500, lineHeight: '1.6', margin: 0 }}>{description}</p>
    </div>
);

// ─── Componente: TestimonialCard ──────────────────────────────────────────────
const TestimonialCard = ({ name, role, text, stars = 5 }) => (
    <div style={{ padding: '28px', backgroundColor: C.white, borderRadius: '16px', border: `1px solid ${C.gray200}`, boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        <div style={{ display: 'flex', gap: '4px', marginBottom: '14px' }}>
            {Array.from({ length: stars }).map((_, i) => (
                <FontAwesomeIcon key={i} icon={faStar} style={{ color: '#f59e0b', fontSize: '13px' }} />
            ))}
        </div>
        <p style={{ fontSize: '14px', color: C.gray600, lineHeight: '1.7', margin: '0 0 16px', fontStyle: 'italic' }}>"{text}"</p>
        <div>
            <p style={{ margin: 0, fontSize: '14px', fontWeight: '700', color: C.gray900 }}>{name}</p>
            <p style={{ margin: 0, fontSize: '12px', color: C.gray400 }}>{role}</p>
        </div>
    </div>
);

// ─── Componente Principal: LandingPage ───────────────────────────────────────
const LandingPage = () => {

    const features = [
        {
            icon: faCalendarCheck,
            title: 'Agenda Inteligente e Prontuário Eletrônico',
            description: [
                'Organize os agendamentos de múltiplos profissionais, com visualização por dia, semana ou mês.',
                'Acesse o prontuário completo do paciente com um clique, registrando histórico, fotos e evoluções.',
                'O perfil Recepção consegue visualizar as agendas de todos os médicos.',
                'O Médico pode atender os pacientes por horário marcado.',
                'Visualize os pacientes por ordem de chegada.',
                'Envie comunicados para aniversariantes e pacientes sem retenção.',
                'Comunicados segmentados por interesses em comum]'],
            mediaType: 'image',
            mediaUrl: featureImage1,
        },
        {
            icon: faChartPie,
            title: 'Gestão Financeira Completa',
            description: [
                'Acompanhe o faturamento dos últimos 7 e 30 dias, além do dia atual.',
                'Analise faturamentos detalhados por serviços, pacientes e períodos.',
                'Gere relatórios de pagamentos recebidos, pendentes e inadimplência.',
                'Obtenha insights com Ticket Médio por paciente e Taxa de Retenção.',
                'Análise completa de custos e despesas para otimizar seus resultados.',
            ],
            mediaType: 'image',
            mediaUrl: featureImage2,
        },
        {
            icon: faBoxesStacked,
            title: 'Controle de Estoque Integrado',
            description: 'Monitore a entrada e saída de materiais automaticamente a cada serviço realizado. Receba alertas de estoque baixo e evite desperdícios. Controle manual disponível para perfis autorizados.',
            mediaType: 'video',
            mediaUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        },
        {
            icon: faUserShield,
            title: 'Segurança e Perfis de Acesso',
            description: 'Tenha controle total sobre quem vê o quê. Crie perfis personalizados (Médico, Recepção, Financeiro) e garanta a segurança e privacidade dos dados da sua clínica.',
            mediaType: 'video',
            mediaUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        },
        {
            icon: faFileMedical,
            title: 'Personalização e Relatórios',
            description: 'Personalize tags de interesse para segmentar pacientes e gere relatórios detalhados sobre faturamento, pagamentos e desempenho da clínica.',
            mediaType: 'video',
            mediaUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
        },
    ];

    const modules = [
        { icon: faStethoscope, name: 'Consultório',      desc: 'Agenda e prontuário',      color: C.primary      },
        { icon: faUsers,       name: 'Equipe',           desc: 'Gestão de colaboradores',  color: '#8b5cf6'      },
        { icon: faBoxesStacked,name: 'Estoque',          desc: 'Controle de materiais',    color: C.teal         },
        { icon: faConciergeBell,name:'Serviços',         desc: 'Catálogo de procedimentos',color: '#f59e0b'      },
        { icon: faChartPie,    name: 'Financeiro',       desc: 'Receitas e despesas',      color: C.success      },
        { icon: faUserShield,  name: 'Perfis de Acesso', desc: 'Segurança e permissões',   color: '#ef4444'      },
    ];

    const testimonials = [
        { name: 'Dra. Ana Paula',    role: 'Dermatologista',       stars: 5, text: 'O FluxoClínica transformou a gestão da minha clínica. A agenda inteligente economiza horas do meu dia.' },
        { name: 'Dr. Carlos Mendes', role: 'Clínico Geral',        stars: 5, text: 'Nunca foi tão fácil acompanhar o financeiro. Os relatórios são claros e me ajudam a tomar decisões.' },
        { name: 'Dra. Fernanda Lima',role: 'Fisioterapeuta',       stars: 5, text: 'O controle de estoque integrado é incrível. Nunca mais fiquei sem material durante os atendimentos.' },
    ];

    const [heroRef, heroInView] = useInView();

    return (
        <div style={{ fontFamily: "'Inter', 'Segoe UI', sans-serif", backgroundColor: C.gray50, overflowX: 'hidden' }}>
            <Navbar />

            {/* ── HERO ── */}
            <section style={{
                minHeight: '100vh',
                background: 'linear-gradient(145deg, #0f172a 0%, #1e3a5f 40%, #1e40af 70%, #2563eb 100%)',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                padding: '120px 24px 80px',
                position: 'relative',
                overflow: 'hidden',
            }}>
                {/* Decorações de fundo */}
                <div style={{ position: 'absolute', top: '10%', left: '5%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '10%', right: '5%', width: '350px', height: '350px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: '800px', height: '800px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(37,99,235,0.08) 0%, transparent 70%)', pointerEvents: 'none' }} />

                {/* Pill badge */}
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '8px 18px', borderRadius: '999px', backgroundColor: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', marginBottom: '28px', backdropFilter: 'blur(8px)' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: C.success, boxShadow: `0 0 8px ${C.success}` }} />
                    <span style={{ fontSize: '13px', fontWeight: '600', color: 'rgba(255,255,255,0.9)' }}>
                        Sistema 100% gratuito para começar
                    </span>
                </div>

                {/* Título principal */}
                <h1 style={{
                    fontSize: 'clamp(32px, 6vw, 64px)',
                    fontWeight: '900',
                    color: C.white,
                    lineHeight: '1.15',
                    margin: '0 0 24px',
                    maxWidth: '800px',
                    letterSpacing: '-0.02em',
                }}>
                    A Gestão da Sua Clínica,{' '}
                    <span style={{ background: 'linear-gradient(135deg, #34d399, #10b981)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                        Simplificada
                    </span>
                </h1>

                <p style={{ fontSize: 'clamp(15px, 2vw, 18px)', color: 'rgba(255,255,255,0.75)', maxWidth: '560px', lineHeight: '1.7', margin: '0 0 40px' }}>
                    Sem mensalidades no plano gratuito. Sem limite de profissionais. Todos os módulos liberados. Foque no que importa: seus pacientes.
                </p>

                {/* CTAs */}
                <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '64px' }}>
                    <Link to="/register" style={{
                        textDecoration: 'none', padding: '15px 32px',
                        background: `linear-gradient(135deg, ${C.success}, ${C.successDark})`,
                        color: C.white, borderRadius: '12px', fontSize: '16px', fontWeight: '800',
                        boxShadow: `0 8px 24px ${C.success}50`,
                        display: 'flex', alignItems: 'center', gap: '8px',
                        transition: 'all 0.2s',
                    }}>
                        Comece Grátis Agora
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                    <a href="#features" style={{
                        textDecoration: 'none', padding: '15px 28px',
                        backgroundColor: 'rgba(255,255,255,0.1)',
                        color: C.white, borderRadius: '12px', fontSize: '15px', fontWeight: '600',
                        border: '1px solid rgba(255,255,255,0.25)',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        backdropFilter: 'blur(8px)',
                    }}>
                        <FontAwesomeIcon icon={faPlay} style={{ fontSize: '12px' }} />
                        Ver funcionalidades
                    </a>
                </div>

                {/* Stats */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '64px' }}>
                    <StatBadge value="200+"  label="Clínicas ativas"       />
                    <StatBadge value="100%"  label="Gratuito para começar" />
                    <StatBadge value="6"     label="Módulos completos"     />
                    <StatBadge value="∞"     label="Profissionais"         />
                </div>

                {/* Trust badges */}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
                    {[
                        { icon: faShieldAlt, text: 'Dados seguros e criptografados' },
                        { icon: faClock,     text: 'Suporte ágil via e-mail'        },
                        { icon: faCheck,     text: 'Sem fidelidade, cancele quando quiser' },
                    ].map(b => (
                        <div key={b.text} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <FontAwesomeIcon icon={b.icon} style={{ color: 'rgba(255,255,255,0.5)', fontSize: '13px' }} />
                            <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.6)' }}>{b.text}</span>
                        </div>
                    ))}
                </div>

                {/* Seta scroll */}
                <div style={{ position: 'absolute', bottom: '32px', left: '50%', transform: 'translateX(-50%)', animation: 'bounce 2s infinite' }}>
                    <div style={{ width: '28px', height: '28px', borderRight: '2px solid rgba(255,255,255,0.4)', borderBottom: '2px solid rgba(255,255,255,0.4)', transform: 'rotate(45deg)' }} />
                </div>
            </section>

            {/* ── MÓDULOS ── */}
            <section id="modules" style={{ padding: '96px 24px', backgroundColor: C.white }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', backgroundColor: `${C.primaryLight}12`, border: `1px solid ${C.primaryLight}25`, marginBottom: '14px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: C.primaryLight, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Todos inclusos</span>
                        </div>
                        <h2 style={{ fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: '800', color: C.gray900, margin: '0 0 14px', letterSpacing: '-0.02em' }}>
                            Tudo que sua clínica precisa
                        </h2>
                        <p style={{ fontSize: '16px', color: C.gray400, maxWidth: '480px', margin: '0 auto', lineHeight: '1.6' }}>
                            6 módulos completos, integrados e prontos para usar desde o primeiro dia.
                        </p>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
                        {modules.map((m, i) => (
                            <ModuleCard key={i} {...m} />
                        ))}
                    </div>
                </div>
            </section>

            {/* ── FUNCIONALIDADES ── */}
            <section id="features" style={{ padding: '96px 24px', backgroundColor: C.gray50 }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '72px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', backgroundColor: `${C.success}12`, border: `1px solid ${C.success}25`, marginBottom: '14px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: C.success, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Funcionalidades</span>
                        </div>
                        <h2 style={{ fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: '800', color: C.gray900, margin: '0 0 14px', letterSpacing: '-0.02em' }}>
                            Poderosas ferramentas para o seu dia a dia
                        </h2>
                        <p style={{ fontSize: '16px', color: C.gray400, maxWidth: '480px', margin: '0 auto', lineHeight: '1.6' }}>
                            Cada funcionalidade foi pensada para economizar seu tempo e aumentar a qualidade do atendimento.
                        </p>
                    </div>

                    {features.map((feature, index) => (
                        <FeatureSection key={index} feature={feature} index={index} />
                    ))}
                </div>
            </section>

            {/* ── DEPOIMENTOS ── */}
            <section style={{ padding: '96px 24px', backgroundColor: C.white }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', backgroundColor: '#fef3c720', border: '1px solid #fde68a50', marginBottom: '14px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Depoimentos</span>
                        </div>
                        <h2 style={{ fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: '800', color: C.gray900, margin: '0 0 14px', letterSpacing: '-0.02em' }}>
                            Quem já usa, aprova
                        </h2>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '20px' }}>
                        {testimonials.map((t, i) => <TestimonialCard key={i} {...t} />)}
                    </div>
                </div>
            </section>

            {/* ── PRICING ── */}
            <section id="pricing" style={{ padding: '96px 24px', backgroundColor: C.gray50 }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <div style={{ textAlign: 'center', marginBottom: '56px' }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 16px', borderRadius: '999px', backgroundColor: `${C.success}12`, border: `1px solid ${C.success}25`, marginBottom: '14px' }}>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: C.success, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Sem surpresas</span>
                        </div>
                        <h2 style={{ fontSize: 'clamp(24px, 4vw, 40px)', fontWeight: '800', color: C.gray900, margin: '0 0 14px', letterSpacing: '-0.02em' }}>
                            Todos os módulos, todos os profissionais
                        </h2>
                        <p style={{ fontSize: '16px', color: C.gray400, maxWidth: '520px', margin: '0 auto', lineHeight: '1.6' }}>
                            Acreditamos que tecnologia para saúde deve ser acessível. Por isso, você começa sem pagar nada.
                        </p>
                    </div>

                    <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '48px' }}>
                        <PricingCard
                            icon={faInfinity}
                            title="Período de Teste"
                            description="200 agendamentos gratuitos para explorar todas as funcionalidades. Sem taxas, sem pegadinhas. Se gostar, escolha um plano."
                            highlight
                        />
                        <PricingCard
                            icon={faUsers}
                            title="Pacientes Ilimitados"
                            description="Cadastre quantos pacientes, recepcionistas e colaboradores precisar, sem custo adicional."
                        />
                        <PricingCard
                            icon={faBoxOpen}
                            title="Todos os Módulos"
                            description="Acesso completo à agenda, prontuário, financeiro, estoque e todas as futuras atualizações."
                        />
                    </div>

                    {/* CTA de pricing */}
                    <div style={{ textAlign: 'center' }}>
                        <Link to="/pricing" style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            padding: '13px 28px',
                            backgroundColor: C.white,
                            color: C.primary,
                            border: `2px solid ${C.primary}`,
                            borderRadius: '12px',
                            textDecoration: 'none',
                            fontSize: '14px', fontWeight: '700',
                            marginRight: '12px',
                            transition: 'all 0.2s',
                        }}>
                            Ver todos os planos
                        </Link>
                        <Link to="/register" style={{
                            display: 'inline-flex', alignItems: 'center', gap: '8px',
                            padding: '13px 28px',
                            background: `linear-gradient(135deg, ${C.success}, ${C.successDark})`,
                            color: C.white,
                            borderRadius: '12px',
                            textDecoration: 'none',
                            fontSize: '14px', fontWeight: '700',
                            boxShadow: `0 4px 14px ${C.success}40`,
                        }}>
                            Começar Grátis
                            <FontAwesomeIcon icon={faArrowRight} />
                        </Link>
                    </div>
                </div>
            </section>

            {/* ── CTA FINAL ── */}
            <section style={{
                padding: '96px 24px',
                background: 'linear-gradient(145deg, #0f172a 0%, #1e3a5f 50%, #1e40af 100%)',
                position: 'relative',
                overflow: 'hidden',
                textAlign: 'center',
            }}>
                <div style={{ position: 'absolute', top: '-80px', left: '-80px', width: '300px', height: '300px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, transparent 70%)' }} />
                <div style={{ position: 'absolute', bottom: '-60px', right: '-60px', width: '280px', height: '280px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,185,129,0.12) 0%, transparent 70%)' }} />

                <div style={{ position: 'relative', zIndex: 1, maxWidth: '640px', margin: '0 auto' }}>
                    <h2 style={{ fontSize: 'clamp(28px, 5vw, 48px)', fontWeight: '900', color: C.white, margin: '0 0 16px', letterSpacing: '-0.02em', lineHeight: '1.2' }}>
                        Pronto para transformar<br />sua clínica?
                    </h2>
                    <p style={{ fontSize: '16px', color: 'rgba(255,255,255,0.7)', margin: '0 0 40px', lineHeight: '1.6' }}>
                        Junte-se a centenas de profissionais que já estão otimizando seu trabalho. Cadastro rápido e acesso imediato.
                    </p>
                    <Link to="/register" style={{
                        display: 'inline-flex', alignItems: 'center', gap: '10px',
                        padding: '16px 36px',
                        background: `linear-gradient(135deg, ${C.success}, ${C.successDark})`,
                        color: C.white, textDecoration: 'none', borderRadius: '14px',
                        fontSize: '17px', fontWeight: '800',
                        boxShadow: `0 8px 32px ${C.success}50`,
                        letterSpacing: '-0.01em',
                    }}>
                        Crie sua conta gratuita agora
                        <FontAwesomeIcon icon={faArrowRight} />
                    </Link>
                    <p style={{ marginTop: '20px', fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
                        Sem cartão de crédito · Acesso imediato · Cancele quando quiser
                    </p>
                </div>
            </section>

            {/* ── FOOTER ── */}
            <footer style={{ backgroundColor: C.gray900, padding: '48px 24px 32px' }}>
                <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '32px', marginBottom: '40px' }}>
                        {/* Brand */}
                        <div style={{ maxWidth: '260px' }}>
                            <img src={logoImage} alt="FluxoClínica" style={{ maxHeight: '40px', marginBottom: '14px', filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
                            <p style={{ fontSize: '13px', color: C.gray400, lineHeight: '1.6', margin: 0 }}>
                                Sistema completo de gestão para clínicas. Simples, moderno e acessível.
                            </p>
                        </div>

                        {/* Links */}
                        {[
                            { title: 'Produto',  links: ['Funcionalidades', 'Módulos', 'Preços', 'Cadastro'] },
                            { title: 'Suporte',  links: ['Central de Ajuda', 'Contato', 'Status do Sistema'] },
                        ].map(col => (
                            <div key={col.title}>
                                <p style={{ margin: '0 0 14px', fontSize: '12px', fontWeight: '700', color: C.gray400, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{col.title}</p>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {col.links.map(link => (
                                        <a key={link} href="#" style={{ textDecoration: 'none', fontSize: '14px', color: C.gray400, transition: 'color 0.2s' }}
                                            onMouseEnter={e => e.target.style.color = C.white}
                                            onMouseLeave={e => e.target.style.color = C.gray400}
                                        >
                                            {link}
                                        </a>
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* CTA no footer */}
                        <div>
                            <p style={{ margin: '0 0 12px', fontSize: '14px', fontWeight: '600', color: C.white }}>Comece hoje mesmo</p>
                            <Link to="/register" style={{
                                display: 'inline-flex', alignItems: 'center', gap: '8px',
                                padding: '11px 22px',
                                background: `linear-gradient(135deg, ${C.success}, ${C.successDark})`,
                                color: C.white, textDecoration: 'none', borderRadius: '10px',
                                fontSize: '14px', fontWeight: '700',
                            }}>
                                Cadastre-se Grátis
                                <FontAwesomeIcon icon={faArrowRight} />
                            </Link>
                        </div>
                    </div>

                    {/* Divider + copyright */}
                    <div style={{ borderTop: `1px solid ${C.gray800}`, paddingTop: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
                        <p style={{ margin: 0, fontSize: '13px', color: C.gray400 }}>
                            © {new Date().getFullYear()} Fluxo Clínica. Todos os direitos reservados.
                        </p>
                        <div style={{ display: 'flex', gap: '20px' }}>
                            {['Privacidade', 'Termos de Uso'].map(l => (
                                <a key={l} href="#" style={{ fontSize: '13px', color: C.gray400, textDecoration: 'none' }}>{l}</a>
                            ))}
                        </div>
                    </div>
                </div>
            </footer>

            {/* ── CSS Global ── */}
            <style>{`
                * { box-sizing: border-box; }

                @keyframes bounce {
                    0%, 100% { transform: translateX(-50%) translateY(0); }
                    50%       { transform: translateX(-50%) translateY(8px); }
                }

                @media (max-width: 768px) {
                    .feature-grid {
                        grid-template-columns: 1fr !important;
                        gap: 32px !important;
                    }
                    .feature-text,
                    .feature-media {
                        order: unset !important;
                    }
                    .nav-links-desktop,
                    .nav-cta-desktop {
                        display: none !important;
                    }
                    .nav-hamburger {
                        display: block !important;
                    }
                }
            `}</style>
        </div>
    );
};

export default LandingPage;
